import {
  STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS,
  STORYTELLING_TYPOGRAPHY_DEFAULT_FAMILIES,
  STORYTELLING_TYPOGRAPHY_DEFAULT_SIZES,
  isStorytellingFontFamily,
  isStorytellingFontSize,
} from "./storytellingFonts";
import {
  DEFAULT_STORYTELLING_HEADER_META,
  serializeStorytellingHeaderMetaForSave,
} from "./storytellingHeaderMeta";

export const DEFAULT_STORYTELLING_DISPLAY = {
  useCustomDesign: false,
  headerMeta: DEFAULT_STORYTELLING_HEADER_META,
  layout: "side-by-side",
  showEntryNumbers: true,
  backgroundStyle: "warm",
  accentColor: "",
  nameFontFamily: STORYTELLING_TYPOGRAPHY_DEFAULT_FAMILIES.name,
  nameFontSize: STORYTELLING_TYPOGRAPHY_DEFAULT_SIZES.name,
  nameColor: "",
  namePartPrimary: "",
  namePartAccent: "",
  nameSplitIndex: null,
  nameAccentColor: "",
  questionFontFamily: STORYTELLING_TYPOGRAPHY_DEFAULT_FAMILIES.question,
  questionFontSize: STORYTELLING_TYPOGRAPHY_DEFAULT_SIZES.question,
  questionColor: "",
  descriptionFontFamily: STORYTELLING_TYPOGRAPHY_DEFAULT_FAMILIES.description,
  descriptionFontSize: STORYTELLING_TYPOGRAPHY_DEFAULT_SIZES.description,
  descriptionColor: "",
};

export const STORYTELLING_BACKGROUND_STYLES = {
  warm: "#ffffff",
  light: "#ffffff",
  neutral: "#f8f8f8",
};

export {
  getStorytellingFontOptions,
  getStorytellingNameStyle,
  getStorytellingNamePartColors,
  getStorytellingQuestionStyle,
  getStorytellingDescriptionStyle,
  getStorytellingTypographyStyle,
  STORYTELLING_FONT_FAMILIES,
  STORYTELLING_FONT_FAMILY_KEYS,
} from "./storytellingFonts";

export const createEmptyStorytellingEntry = () => ({
  question: "",
  description: "",
  image: null,
  chapterLabel: "",
});

export const normalizeStorytellingList = (raw) => {
  if (Array.isArray(raw)) {
    if (raw.length === 0) return [createEmptyStorytellingEntry()];
    return raw.map((item) => ({
      question: item?.question || "",
      description: item?.description || "",
      image: item?.image || null,
      chapterLabel: item?.chapterLabel || "",
    }));
  }

  if (
    raw &&
    typeof raw === "object" &&
    (raw.question || raw.description || raw.image)
  ) {
    return [
      {
        question: raw.question || "",
        description: raw.description || "",
        image: raw.image || null,
        chapterLabel: raw.chapterLabel || "",
      },
    ];
  }

  return [createEmptyStorytellingEntry()];
};

const pickFontFamily = (value, fallback) =>
  isStorytellingFontFamily(value) ? value : fallback;

const pickFontSize = (value, fallback) =>
  isStorytellingFontSize(value) ? value : fallback;

const parseNameSplitIndex = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
};

const normalizeRawHeaderMeta = (raw) => serializeStorytellingHeaderMetaForSave(raw);

export const normalizeStorytellingDisplay = (raw, menuButtonColor) => {
  const base = { ...DEFAULT_STORYTELLING_DISPLAY };
  if (!raw || typeof raw !== "object") {
    return {
      ...base,
      useCustomDesign: false,
      accentColor: menuButtonColor || "",
      nameColor: STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.name,
      namePartPrimary: "",
      namePartAccent: "",
      nameSplitIndex: null,
      nameAccentColor: "",
      questionColor: STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.question,
      descriptionColor: STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.description,
    };
  }

  return {
    useCustomDesign: raw.useCustomDesign === true,
    headerMeta: normalizeRawHeaderMeta(raw.headerMeta),
    layout: ["side-by-side", "stacked", "alternating"].includes(raw.layout)
      ? raw.layout
      : base.layout,
    showEntryNumbers: raw.showEntryNumbers !== false,
    backgroundStyle: ["warm", "light", "neutral"].includes(raw.backgroundStyle)
      ? raw.backgroundStyle
      : base.backgroundStyle,
    accentColor:
      String(raw.accentColor || menuButtonColor || "").trim() ||
      menuButtonColor ||
      "",
    nameFontFamily: pickFontFamily(raw.nameFontFamily, base.nameFontFamily),
    nameFontSize: pickFontSize(raw.nameFontSize, base.nameFontSize),
    nameColor:
      String(raw.nameColor || "").trim() || STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.name,
    namePartPrimary: String(raw.namePartPrimary || "").trim(),
    namePartAccent: String(raw.namePartAccent || "").trim(),
    nameSplitIndex: parseNameSplitIndex(raw.nameSplitIndex),
    nameAccentColor: String(raw.nameAccentColor || "").trim(),
    questionFontFamily: pickFontFamily(
      raw.questionFontFamily,
      base.questionFontFamily
    ),
    questionFontSize: pickFontSize(raw.questionFontSize, base.questionFontSize),
    questionColor:
      String(raw.questionColor || "").trim() ||
      STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.question,
    descriptionFontFamily: pickFontFamily(
      raw.descriptionFontFamily,
      base.descriptionFontFamily
    ),
    descriptionFontSize: pickFontSize(
      raw.descriptionFontSize,
      base.descriptionFontSize
    ),
    descriptionColor:
      String(raw.descriptionColor || "").trim() ||
      STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.description,
  };
};

export const cloneStorytellingDisplay = (display) => ({
  ...display,
  headerMeta: {
    city: { ...display.headerMeta?.city },
    founded: { ...display.headerMeta?.founded },
    type: { ...display.headerMeta?.type },
    cuisine: { ...display.headerMeta?.cuisine },
  },
});

export const getBuiltinStorytellingDisplay = (menuButtonColor) => ({
  useCustomDesign: false,
  headerMeta: serializeStorytellingHeaderMetaForSave(DEFAULT_STORYTELLING_HEADER_META),
  layout: "side-by-side",
  showEntryNumbers: true,
  backgroundStyle: "warm",
  accentColor: String(menuButtonColor || "#c1a37a").trim(),
  nameFontFamily: "display",
  nameFontSize: "large",
  nameColor: "#000000",
  namePartPrimary: "",
  namePartAccent: "",
  nameSplitIndex: null,
  nameAccentColor: "",
  questionFontFamily: "display",
  questionFontSize: "medium",
  questionColor: "#000000",
  descriptionFontFamily: "sans",
  descriptionFontSize: "medium",
  descriptionColor: "#475569",
});

export const getEffectiveStorytellingDisplay = (raw, menuButtonColor) => {
  const normalized = normalizeStorytellingDisplay(raw, menuButtonColor);
  if (!normalized.useCustomDesign) {
    return getBuiltinStorytellingDisplay(menuButtonColor);
  }
  return normalized;
};

export const reindexStorytellingMap = (map, removedIndex) => {
  const next = {};
  Object.entries(map || {}).forEach(([key, value]) => {
    const index = Number(key);
    if (Number.isNaN(index)) return;
    if (index < removedIndex) next[index] = value;
    else if (index > removedIndex) next[index - 1] = value;
  });
  return next;
};

export const storytellingHasContent = (list) =>
  normalizeStorytellingList(list).some(
    (item) =>
      String(item.question || "").trim() ||
      String(item.description || "").trim() ||
      item.image
  );
